import {Component, Input} from "@angular/core";
import {CommonModule} from "@angular/common";
import {RouterModule} from "@angular/router";
import {MatTableModule} from "@angular/material/table";
import {MalfunctionStatus} from "../../../../shared/interfaces/report";
import {getTimeZoneString} from "../../../../shared/helpers/dateTime/getTimeZoneString";
import {PAGE_SIZE} from "./table-config";
import {calcTimeDifference} from "../../../../shared/helpers/dateTime/calcTimeDifference";

@Component({
  selector: "app-report-table",
  standalone: true,
  imports: [CommonModule, RouterModule, MatTableModule],
  template: `
    <div class="section-heading my-3 w-100">
      <div [ngClass]="tableHeadingClass">
        <span>{{ tableHeading | uppercase }}</span>
      </div>
      <span class="qundis-ubuntu-14 mt-5">{{ tableSubHeading }}</span>
      <div class="active-report-table" *ngIf="dataSource.length > 0; else noReports">
        <!-- only display 5 entries - is that what we want for all tables? -->
        <table
          mat-table
          [dataSource]="this.displayAll ? dataSource : dataSource.slice(0,PAGE_SIZE)"
          class="mat-report-table font-base"
          [class]="tableWidth"
        >
          <!-- ID Column -->
          <ng-container matColumnDef="id">
            <th mat-header-cell [style.width]="getColumnWidth('id')" *matHeaderCellDef>Stillstands-ID</th>
            <td mat-cell [style.width]="getColumnWidth('id')" *matCellDef="let report">
              {{ report.incident_id }}
            </td>
          </ng-container>

          <!-- Workplace Column -->
          <ng-container matColumnDef="workplace">
            <th mat-header-cell [style.width]="getColumnWidth('workplace')" *matHeaderCellDef>Arbeitsplatz</th>
            <td mat-cell [style.width]="getColumnWidth('workplace')" *matCellDef="let report">
              {{ report.workplace.name }}
            </td>
          </ng-container>

          <!-- Category Column -->
          <ng-container matColumnDef="category">
            <th mat-header-cell [style.width]="getColumnWidth('category')" *matHeaderCellDef>Prozessschritt</th>
            <td mat-cell [style.width]="getColumnWidth('category')" *matCellDef="let report">
              {{ report.category.name }}
            </td>
          </ng-container>

          <!-- Time Column -->
          <ng-container matColumnDef="time">
            <th mat-header-cell [style.width]="getColumnWidth('time')" *matHeaderCellDef>Uhrzeit</th>
            <td mat-cell [style.width]="getColumnWidth('time')" *matCellDef="let report">
              {{
                report.created_at
                  | date : "HH:mm:ss" : getTimeZoneString() : "de"
              }}
            </td>
          </ng-container>

          <!-- Malfunction Column -->
          <ng-container matColumnDef="reason">
            <th mat-header-cell [style.width]="getColumnWidth('reason')" *matHeaderCellDef>Grund des Stillstandes</th>
            <td mat-cell [style.width]="getColumnWidth('reason')" *matCellDef="let report">
              {{ report.malfunction.name }}
            </td>
          </ng-container>

          <!-- Status Column -->
          <ng-container matColumnDef="status">
            <th mat-header-cell [style.width]="getColumnWidth('status')" *matHeaderCellDef>Status</th>
            <td mat-cell [style.width]="getColumnWidth('status')" *matCellDef="let report">
              {{ getReportStatus(report.status) }}
            </td>
          </ng-container>

          <!-- Duration Column -->
          <ng-container matColumnDef="duration">
            <th mat-header-cell [style.width]="getColumnWidth('duration')" *matHeaderCellDef>Dauer (hh:mm)</th>
            <td mat-cell [style.width]="getColumnWidth('duration')" *matCellDef="let report">
              {{ getReportDuration(report.created_at, report.solved_on) }}
            </td>
          </ng-container>

          <!-- Occurrence Count Column -->
          <ng-container matColumnDef="occurrences">
            <th mat-header-cell [style.width]="getColumnWidth('occurrences')" *matHeaderCellDef>Häufigkeit</th>
            <td mat-cell [style.width]="getColumnWidth('occurrences')" *matCellDef="let report">
              {{ report.occurrence_count }}
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            [routerLink]="['/report', row.id]"
            class="cursor-pointer"
          ></tr>
        </table>
      </div>
    </div>
    <ng-template #noReports>
      <div class="mt-3 mb-2">
      <span>
      Zur Zeit gibt es keine Stillstandsmeldungen in dieser Kategorie
      </span>
      </div>
    </ng-template>

  `,
  styles: [`
    .mat-mdc-row {
      font-family: "Ubuntu";
      font-weight: 400;
      font-size: 20px;
      line-height: 23px;
    }

    .mat-mdc-header-cell {
      padding-left: 0;
      border: none;
      font-family: "Ubuntu";
      font-size: 14px;
      line-height: 16px;
      color: hsl(0, 0%, 62%, 1);
    }

    .mat-mdc-cell {
      padding-left: 0;
      border: none;
      text-decoration: underline;
    }

    .mat-mdc-row:hover .mat-mdc-cell {
      background-color: hsla(0, 0%, 0%, 0.08);
    }

    .mat-report-table {
      table-layout: fixed;
      width: 100%;
    }

    .equal-width .mat-mdc-header-cell,
    .equal-width .mat-mdc-cell {
      width: var(--equal-column-width, auto);
    }
  `
  ],
})
export class ReportTableComponent {
  @Input() dataSource: any[] = [];
  @Input() displayAll: boolean = false;
  @Input() tableWidth: "w-50" | "w-75" | "w-100" = "w-75";
  @Input() tableHeading: string = "";
  @Input() tableHeadingClass: string = "";
  @Input() tableSubHeading: string = "";
  @Input() displayedColumns: string[] = [];
  @Input() columnWidths: string | { [key: string]: string } = 'equal';

  protected readonly getTimeZoneString = getTimeZoneString;

  getReportStatus(status: MalfunctionStatus) {
    switch (status) {
      case MalfunctionStatus.CREATED:
        return "aktiv"
      case MalfunctionStatus.SOLVED:
        return "gelöst";
      case MalfunctionStatus.ESCALATED:
        return "eskaliert";
      case MalfunctionStatus.CANCELED:
        return "verworfen";
      default:
        return "erfasst";
    }
  }

  getReportDuration(created_at: string, solved_on: string) {
    return calcTimeDifference(created_at, solved_on)
  }

  getColumnWidth(column: string): string {
    if (this.columnWidths === 'equal') {
      return 'var(--equal-column-width)';
    }

    if (typeof this.columnWidths === 'object' && this.columnWidths[column]) {
      return this.columnWidths[column];
    }

    return 'auto';
  }

  protected readonly PAGE_SIZE = PAGE_SIZE;
}
